import type {
  MoveAction,
  RunHorsesAction,
  RunHorsesState,
  ValidationResult,
} from "../types";
import {
  getCell,
  getKnightTargets,
  getSlideTargets,
  isInBounds,
  isPlayerId,
  positionsEqual,
} from "../utils";

const ok: ValidationResult = { valid: true };

function fail(
  error: string,
  details?: Record<string, unknown>,
): ValidationResult {
  return { valid: false, error, details };
}

function validateActor(
  state: RunHorsesState,
  playerId: string,
): ValidationResult {
  if (!isPlayerId(playerId)) {
    return fail("Unknown player", { playerId });
  }
  if (state.phase === "game-over") {
    return fail("Game is already over");
  }
  if (state.currentPlayerId !== playerId) {
    return fail("It is not this player's turn", {
      playerId,
      currentPlayerId: state.currentPlayerId,
    });
  }
  return ok;
}

export function validateMove(
  state: RunHorsesState,
  action: MoveAction,
): ValidationResult {
  const actor = validateActor(state, action.playerId);
  if (!actor.valid) return actor;

  const { from, to, kind } = action;

  if (!isInBounds(from.row, from.col)) {
    return fail("Source is outside the board", { from });
  }
  if (!isInBounds(to.row, to.col)) {
    return fail("Destination is outside the board", { to });
  }
  if (getCell(state.board, from.row, from.col) !== action.playerId) {
    return fail("No own horse at the source cell", { from });
  }
  if (getCell(state.board, to.row, to.col) !== "empty") {
    return fail("Destination cell is occupied", { to });
  }
  if (positionsEqual(from, to)) {
    return fail("Move must change position", { from, to });
  }

  const legalTargets =
    kind === "slide"
      ? getSlideTargets(state.board, from)
      : getKnightTargets(state.board, from);
  if (!legalTargets.some((target) => positionsEqual(target, to))) {
    return fail(`Illegal ${kind} move`, { from, to, kind });
  }

  return ok;
}

export function validateAction(
  state: RunHorsesState,
  action: RunHorsesAction,
): ValidationResult {
  switch (action.type) {
    case "Move":
      return validateMove(state, action);
  }
}
